import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import { Typography, Tooltip } from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ContactStateModal from "../ContactStateModal";
import ContactStateHistory from "../ContactStateHistory";
import useContactState from "../../hooks/useContactState";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles(() => ({
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    padding: "4px 10px",
    borderRadius: 8,
    border: "0.5px solid rgba(0,0,0,0.18)",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 500,
    lineHeight: 1,
    whiteSpace: "nowrap",
    userSelect: "none",
    background: "transparent",
    transition: "background 0.15s",
    "&:hover": { background: "rgba(0,0,0,0.04)" },
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    flexShrink: 0,
  },
  chevron: {
    fontSize: 16,
    marginLeft: -2,
    opacity: 0.7,
  },
}));

const ContactStateChip = ({ ticket, onStateChanged, compact = false }) => {
  const classes = useStyles();
  const history = useHistory();
  const [modalOpen, setModalOpen] = useState(false);

  const {
    currentState,
    states,
    lostReasons,
    loading,
    changeState,
    closeOnGained,
    closeOnLost,
  } = useContactState(
    ticket?.contact?.id,
    ticket?.contact?.contactState
  );

  const stateColor = currentState?.color || "#9E9E9E";
  const stateName = currentState?.name || i18n.t("contactStateModal.noState");

  const handleStateChanged = async (newState) => {
    if (onStateChanged) onStateChanged(newState);
    // Auto-resolve ticket based on company close settings
    const sysId = newState?.systemId;
    const shouldClose =
      (sysId === 4 && closeOnGained) ||
      (sysId === 5 && closeOnLost);
    // feat_395: el cierre lo hace el BACKEND (autoClose:true en el PUT de
    // estado, feat_394) — acá sólo se sale del chat. Antes este componente
    // cerraba con su propio PUT /tickets y, con autoClose, habría doble cierre.
    if (shouldClose && ticket?.status !== "closed") {
      history.push("/tickets");
    }
  };

  const changeStateAutoClose = (stateId, lostReasonId, opts = {}) =>
    changeState(stateId, lostReasonId, { ...opts, autoClose: true, source: "ui" });

  if (!ticket?.contact?.id) return null;

  return (
    <>
      <Tooltip title={i18n.t("contactStateModal.chipTooltip")} arrow>
        <span
          className={classes.chip}
          style={{
            color: stateColor,
            ...(compact ? { fontSize: 11, padding: "3px 6px", gap: 3 } : {}),
          }}
          onClick={() => setModalOpen(true)}
        >
          <span className={classes.dot} style={{ backgroundColor: stateColor }} />
          <Typography variant="inherit">{stateName}</Typography>
          <ExpandMoreIcon className={classes.chevron} style={compact ? { fontSize: 14 } : undefined} />
        </span>
      </Tooltip>
      {/* feat_395: historial de cambios de estado (feat_394) en un popover. */}
      <ContactStateHistory contactId={ticket.contact.id} />

      <ContactStateModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        contactId={ticket.contact.id}
        currentState={currentState}
        states={states}
        lostReasons={lostReasons}
        loading={loading}
        changeState={changeStateAutoClose}
        onStateChanged={(newState) => {
          setModalOpen(false);
          handleStateChanged(newState);
        }}
      />
    </>
  );
};

export default ContactStateChip;
